import { createContext, useState, useEffect, useContext, useRef } from 'react'
import { Play, Pause, RotateCcw } from 'lucide-react' 

import { ReplaySessionContext } from './ReplaySessionContext'

export const ReplayPlaybackControllerContext = createContext()

export const ReplayPlaybackControllerProvider = ({ 
  children, 
  onTimeUpdate, 
  taggedBlocks = [],
  activeRecording = null,
  // Selection props
  selectionMode = false,
  timelineSelection = null,
  onSelectionChange = null
}) => {
  const { session } = useContext(ReplaySessionContext)
  const duration = session?.duration || 0

  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [playbackSpeed, setPlaybackSpeed] = useState(1)

  // Selection state
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState(null)
  const [dragEnd, setDragEnd] = useState(null)
  const timelineRef = useRef(null)

  // Call onTimeUpdate whenever currentTime changes
  useEffect(() => {
    if (onTimeUpdate) {
      onTimeUpdate(currentTime)
    }
  }, [currentTime, onTimeUpdate])

  useEffect(() => {
    let interval
    if (isPlaying && duration > 0) {
      interval = setInterval(() => {
        setCurrentTime(prev => {
           const nextTime = prev + (100 * playbackSpeed)
           
           if (nextTime >= duration) {
             setIsPlaying(false)
             return duration
           }
           
           return nextTime
        })
      }, 100)
    }
    return () => clearInterval(interval)
  }, [isPlaying, playbackSpeed, duration])

  const togglePlay = () => {
      if (currentTime >= duration) {
          setCurrentTime(0)
      }
      setIsPlaying(!isPlaying)
  }
  
  const handleSeek = (e) => {
      if (selectionMode) return // Don't seek when in selection mode
      const newTime = Number(e.target.value)
      setCurrentTime(newTime)
  }

  const reset = () => {
    setIsPlaying(false)
    setCurrentTime(0)
  }

  const formatTime = (ms) => {
    if (!ms) return "0:00"
    const safeMs = Math.max(0, ms)
    const totalSeconds = Math.floor(safeMs / 1000)
    const m = Math.floor(totalSeconds / 60)
    const s = totalSeconds % 60
    return `${m}:${s < 10 ? '0' : ''}${s}`
  }

  const currentProgress = duration > 0 ? (currentTime / duration) * 100 : 0

  // Convert pixel position to time
  const pixelToTime = (pixelX) => {
    if (!timelineRef.current || duration <= 0) return 0
    const rect = timelineRef.current.getBoundingClientRect()
    const relativeX = Math.max(0, Math.min(pixelX - rect.left, rect.width))
    const percentage = relativeX / rect.width
    return Math.round(percentage * duration)
  }

  // Handle mouse down - start selection
  const handleMouseDown = (e) => {
    if (!selectionMode) {
      // Normal click-to-seek behavior
      const time = pixelToTime(e.clientX)
      setCurrentTime(time)
      return
    }
    
    const time = pixelToTime(e.clientX)
    setIsDragging(true)
    setDragStart(time)
    setDragEnd(time)
  }

  // Handle mouse move - update selection
  const handleMouseMove = (e) => {
    if (!isDragging || !selectionMode) return
    
    const time = pixelToTime(e.clientX)
    setDragEnd(time)
  }

  // Handle mouse up - finalize selection
  const handleMouseUp = () => {
    if (!isDragging || !selectionMode) return
    
    setIsDragging(false)
    
    if (dragStart !== null && dragEnd !== null) {
      const startTime = Math.min(dragStart, dragEnd)
      const endTime = Math.max(dragStart, dragEnd)
      
      // Only create selection if it's meaningful (at least 500ms)
      if (endTime - startTime >= 500 && onSelectionChange) {
        onSelectionChange({ startTime, endTime })
      }
    }
    
    setDragStart(null)
    setDragEnd(null)
  }

  // Handle mouse leave
  const handleMouseLeave = () => {
    if (isDragging) {
      handleMouseUp()
    }
  }

  // Calculate current drag selection
  const getDragSelection = () => {
    if (!isDragging || dragStart === null || dragEnd === null) return null
    
    const startTime = Math.min(dragStart, dragEnd)
    const endTime = Math.max(dragStart, dragEnd)
    const left = (startTime / duration) * 100
    const width = ((endTime - startTime) / duration) * 100
    
    return { left, width }
  }

  // Get saved selection position for display
  const getSelectionPosition = () => {
    if (!timelineSelection) return null
    
    const left = (timelineSelection.startTime / duration) * 100
    const width = ((timelineSelection.endTime - timelineSelection.startTime) / duration) * 100
    
    return { left, width }
  }

  const dragSelection = getDragSelection()
  const savedSelection = getSelectionPosition()

  return (
    <ReplayPlaybackControllerContext.Provider value={{
      isPlaying,
      currentTime,
      duration,
      playbackSpeed,
      setIsPlaying,
      setCurrentTime,
      setPlaybackSpeed
    }}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden'
      }}>
        {/* Children take remaining space */}
        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {children}
        </div>

        {/* BOTTOM CONTROLS */}
        <div style={{
          height: '80px',
          flexShrink: 0,
          width: '100%',
          boxSizing: 'border-box',  
          backgroundColor: '#1f2937', 
          borderTop: '1px solid #374151',
          display: 'flex',
          flexDirection: 'column',
          padding: '8px 24px 12px 24px',
          color: '#f3f4f6',
          gap: '8px',
          boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.3)'
        }}>
          
          {/* Timeline Label Row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#9ca3af', fontSize: '11px', fontWeight: 500 }}>
                Behavioral Timeline
              </span>
              {taggedBlocks.length > 0 && (
                <span style={{ 
                  fontSize: '10px', 
                  color: '#6b7280',
                  backgroundColor: '#374151',
                  padding: '2px 6px',
                  borderRadius: '4px'
                }}>
                  {taggedBlocks.length} segments
                </span>
              )}
              {selectionMode && (
                <span style={{ 
                  fontSize: '10px', 
                  color: '#fbbf24',
                  backgroundColor: '#78350f',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontWeight: 600
                }}>
                  SELECTION MODE
                </span>
              )}
            </div>
            <span style={{ color: '#9ca3af', fontSize: '11px' }}>
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>

          {/* Controls + Timeline Row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            
            {/* Controls Group */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button onClick={reset} style={btnStyle} title="Reset">
                    <RotateCcw size={16} />
                </button>
                
                <button onClick={togglePlay} style={btnStyle} title={isPlaying ? "Pause" : "Play"}>
                    {isPlaying ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" />}
                </button>
            </div>

            {/* Timeline Bar with Tagged Blocks and Selection */}
            <div 
              ref={timelineRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              style={{ 
                flex: 1, 
                position: 'relative', 
                height: '24px',
                backgroundColor: '#374151',
                borderRadius: '4px',
                overflow: 'hidden',
                cursor: selectionMode ? 'crosshair' : 'pointer',
                border: selectionMode ? '2px solid #f59e0b' : 'none',
                boxSizing: 'border-box',
                userSelect: 'none'
              }}
            >
              
              {/* Tagged Behavior Blocks */}
              {taggedBlocks.map((block, index) => {
                const left = (block.startTime / duration) * 100
                const width = ((block.endTime - block.startTime) / duration) * 100
                
                return (
                  <div
                    key={block.id || index}
                    title={`${block.behavior?.label || 'Behavior'} (${formatTime(block.startTime)} - ${formatTime(block.endTime)})`}
                    style={{
                      position: 'absolute',
                      left: `${left}%`,
                      width: `${Math.max(width, 0.5)}%`,
                      height: '100%',
                      backgroundColor: block.behavior?.color || '#f59e0b',
                      opacity: 0.8,
                      cursor: selectionMode ? 'crosshair' : 'pointer',
                      zIndex: 5,
                    }}
                  />
                )
              })}

              {/* Active Recording Indicator */}
              {activeRecording && (
                <div 
                  style={{
                    position: 'absolute',
                    left: `${(activeRecording.startTime / duration) * 100}%`,
                    width: `${((currentTime - activeRecording.startTime) / duration) * 100}%`,
                    height: '100%',
                    backgroundColor: activeRecording.behavior?.color || '#ef4444',
                    opacity: 0.6,
                    animation: 'pulse 1s infinite',
                    zIndex: 6
                  }}
                />
              )}

              {/* Current Drag Selection (while dragging) */}
              {dragSelection && (
                <div 
                  style={{
                    position: 'absolute',
                    left: `${dragSelection.left}%`,
                    width: `${dragSelection.width}%`,
                    height: '100%',
                    backgroundColor: '#f59e0b',
                    opacity: 0.5,
                    zIndex: 25,
                    pointerEvents: 'none',
                    border: '2px solid #fbbf24',
                    boxSizing: 'border-box'
                  }}
                />
              )}

              {/* Saved Selection (after mouse up) */}
              {savedSelection && !isDragging && (
                <div 
                  style={{
                    position: 'absolute',
                    left: `${savedSelection.left}%`,
                    width: `${savedSelection.width}%`,
                    height: '100%',
                    backgroundColor: '#f59e0b',
                    opacity: 0.6,
                    zIndex: 24,
                    pointerEvents: 'none',
                    border: '2px solid #fbbf24',
                    boxSizing: 'border-box'
                  }}
                />
              )}
              
              {/* Current Time Indicator (Playhead) */}
              <div style={{
                position: 'absolute',
                left: `${currentProgress}%`,
                top: 0,
                bottom: 0,
                width: '3px',
                backgroundColor: '#ffffff',
                boxShadow: '0 0 6px rgba(255,255,255,0.8)',
                zIndex: 30,
                pointerEvents: 'none',
                transform: 'translateX(-50%)'
              }} />
            </div>

            {/* Speed Toggle */}
            <button 
                onClick={() => setPlaybackSpeed(s => s === 1 ? 2 : s === 2 ? 5 : 1)}
                style={{ ...btnStyle, fontSize: '12px', minWidth: '40px', fontWeight: 'bold' }}
                title="Playback Speed"
            >
                {playbackSpeed}x
            </button>

          </div>
        </div>

        {/* Pulse animation for active recording */}
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 0.6; }
            50% { opacity: 0.3; }
          }
        `}</style>
      </div>
    </ReplayPlaybackControllerContext.Provider>
  )
}

const btnStyle = {
    background: 'none', 
    border: 'none', 
    color: 'inherit', 
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px',
    borderRadius: '4px',
    transition: 'background 0.2s'
}